import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import LeadsquaredHeader from "@components/LandingPageComponents/LeadsquaredHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-1.svg",
        title: "Sync LeadSquared Contacts with WhatsApp",
        description: "Offer instant support and personalised responses with a seamless data sync between LeadSquared and Gallabox",
    },
    {
        img: "https://assets.gallabox.com/gb-home/personalized-card.png",
        title: "Improve Engagement and Response Rates",
        description: "Make conversations more engaging with media elements (images, videos, and more), increasing response rates",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-3.svg",
        title: "Keep Customers Informed with Proactive Alerts",
        description: "Share WhatsApp reminders based on event or triggers in LeadSquared CRM and reduce repetitive support queries ",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-4.svg",
        title: "Turn Contacts Into Brand Ambassadors",
        description: "Nudge your LeadSquared contacts with the right messaging and turn them into repeat customers",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "What is LeadSquared CRM WhatsApp integration?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "The LeadSquared CRM WhatsApp integration powered by Gallabox empowers you to engage and connect with existing and new LeadSquared contacts on WhatsApp more effectively and efficiently.",
            },
        },
        {
            "@type": "Question",
            name: "Why should I integrate CRM with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "To integrate WhatsApp with your LeadSquared CRM, you need a WhatsApp Automation Solution like Gallabox. Gallabox offers a one-tap integration which will connect your WhatsApp Business API with LeadSquared in just four steps:1.Search for LeadSquared CRM connector in our marketplace,2.Install the app, enter your credentials, and authenticate as requested,3.Distribute the integration to the users who need to access it,4.Integrate the two systems to facilitate two-way sync and break down data silos.Once you've integrated Gallabox with your LeadSquared CRM, you can take advantage of all the benefits that this powerful combination has to offer.",
            },
        },

        {
            "@type": "Question",
            name: "What are the benefits of integrating LeadSquared with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Connecting your WhatsApp and LeadSquared accounts will help you shorten your sales cycle, make the most out of your marketing campaigns, boost engagement and response rates, and add a human touch to automated conversations.",
            },
        },
        {
            "@type": "Question",
            name: "How does LeadSquared WhatsApp CRM integration work?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "To integrate WhatsApp with your LeadSquared CRM, you need a WhatsApp Automation Solution like Gallabox. Gallabox offers a one-tap integration which will connect your WhatsApp Business API with LeadSquared in just four steps:1.Search for LeadSquared CRM connector in our marketplace,2.Install the app, enter your credentials, and authenticate as requested,3.Distribute the integration to the users who need to access it,4.Integrate the two systems to facilitate two-way sync and break down data silos.Once you've integrated Gallabox with your LeadSquared CRM, you can take advantage of all the benefits that this powerful combination has to offer.",
            },
        },
        {
            "@type": "Question",
            name: "How to automate customer engagement with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Enhance your customer communication experience with Gallabox’s native integration. This powerful extension seamlessly integrates Gallabox with LeadSquared, allowing you to effortlessly send WhatsApp reminders to customers and prospects via WhatsApp.",
            },
        },
    ],
};

const LeadSquared: React.FC = () => {
    return (
        <NewLayout
            title="Leadsquared WhatsApp Integration | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/leadsquared/leadsquared-og-new.jpg" }}
            canonical="https://gallabox.com/whatsapp-integrations/lead-squared"
            description="Engage and convert leads faster with LeadSquared WhatsApp integration by Gallabox. Automate CRM chats and follow-ups."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <div className="bg-white">
                <LeadsquaredHeader />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/leadsquared/section1.png"
                    title="Integrate LeadSquared Converse"
                    description="Get a direct line to your customer base and chat with your customers and prospects directly from LeadSquared. Expand the reach of your business by offering your contacts everything from support to payment on WhatsApp."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/leadsquared/section2.png"
                    title="Blast Bulk Broadcasts Instantly"
                    description="Send bulk messages like important product updates, seasonal promotions, and more to multiple customers in just a click. Share the right offers and promotions at the right time and nudge them along the sales funnel swiftly."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/leadsquared/section3.png"
                    title="WhatsApp Marketing Automation"
                    description="Trigger workflows from your LeadSquared CRM to extract customer or prospect information like location, preferences, purchase history, and more. Equipped with data, run targeted marketing campaigns that turn your leads into deals."
                    isImageRightSide
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/leadsquared/section4.png"
                    title="Ensure Transparency in Operations"
                    description="Get a unified dashboard that displays all your conversations and prospective opportunities with the shared inbox feature. Leave private notes to employees within the chat, tag team members, and assign or unassign a conversation."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/leadsquared/section5.png"
                    title="Track and Manage Leads Effectively"
                    description="Keep a close eye on your WhatsApp conversations by tracking the message delivery status and read receipts. Gauge the engagement level of your leads using these metrics and put together a more effective follow-up strategy."
                    isImageRightSide
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Turn WhatsApp Into a Profitable Sales Channel with LeadSquared Integration</h3>}
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <YellowCTAhref
                    customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Unlock Operational Efficiency with Gallabox and LeadSquared Integration</h3>}
                    buttonText="Get Started Today"
                />
                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about LeadSquared-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="leadsquared-landing-page"
                        items={[
                            {
                                question: "What is LeadSquared CRM WhatsApp integration?",
                                answer: "The LeadSquared CRM WhatsApp integration powered by Gallabox empowers you to engage and connect with existing and new LeadSquared contacts on WhatsApp more effectively and efficiently.",
                            },
                            {
                                question: "Why should I integrate CRM with WhatsApp?",
                                answer: (
                                    <>
                                        <span>{"With a WhatsApp CRM integration, you can:"}</span>

                                        <ol className="pl-1">
                                            <li>{"Sync leads, activities, and more instantly."}</li>
                                            <li>{"Automate your customer service workflows."}</li>
                                            <li>{"Track interactions across multiple channels."}</li>
                                            <li>{"Make conversations more personalised."}</li>
                                            <li>{"Amplify sales and boost revenue."}</li>
                                        </ol>
                                    </>
                                ),
                            },
                            {
                                question: "What are the benefits of integrating LeadSquared with WhatsApp?",
                                answer: "Connecting your WhatsApp and LeadSquared accounts will help you shorten your sales cycle, make the most out of your marketing campaigns, boost engagement and response rates, and add a human touch to automated conversations.",
                            },
                            {
                                question: "How does LeadSquared WhatsApp CRM integration work?",
                                answer: (
                                    <>
                                        <p>
                                            {
                                                "To integrate WhatsApp with your LeadSquared CRM, you need a WhatsApp Automation Solution like Gallabox. Gallabox offers a one-tap integration which will connect your WhatsApp Business API with LeadSquared in just four steps:"
                                            }
                                        </p>

                                        <ol className="pl-1">
                                            <li>{"Search for LeadSquared CRM connector in our marketplace."}</li>
                                            <li>{"Install the app, enter your credentials, and authenticate as requested."}</li>
                                            <li>{"Distribute the integration to the users who need to access it."}</li>
                                            <li>{"Integrate the two systems to facilitate two-way sync and break down data silos."}</li>
                                        </ol>

                                        <p>
                                            {
                                                "Once you've integrated Gallabox with your LeadSquared CRM, you can take advantage of all the benefits that this powerful combination has to offer."
                                            }
                                        </p>
                                    </>
                                ),
                            },
                            {
                                question: "How to automate customer engagement with WhatsApp?",
                                answer: "Enhance your customer communication experience with Gallabox’s native integration. This powerful extension seamlessly integrates Gallabox with LeadSquared, allowing you to effortlessly send WhatsApp reminders to customers and prospects via WhatsApp.",
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default LeadSquared;
